// ABOUTME: Turns the URLs and e-mail addresses inside a plain string into elements produced by a caller-supplied renderer.
// ABOUTME: Replaces react-linkify (unmaintained since 2018, vulnerable linkify-it) for read-only text.
import type { ReactNode } from "react"

export type RenderLink = (href: string, text: string, key: number) => ReactNode

// Candidates are runs without whitespace or `<`/`>` (linear scan); whether a run is a link is decided in code
// below, so no regex has to describe URLs or e-mail addresses with nested quantifiers.
const TOKEN_PATTERN = /[^\s<>]+/g
// Punctuation that ends a sentence around a link is not part of it (stripped with a loop: `[…]+$` backtracks).
const TRAILING_PUNCTUATION = new Set([".", ",", ";", ":", "!", "?", "'", '"'])
// A closing bracket belongs to the link only when the link itself opened it: `(https://x/y_(z))` vs `(https://x/y)`.
const BRACKET_PAIRS: Partial<Record<string, string>> = { ")": "(", "]": "[", "}": "{" }

const count = (s: string, ch: string): number => s.split(ch).length - 1

const trimLinkEnd = (raw: string): string => {
  let s = raw
  while (s.length > 0) {
    const last = s.charAt(s.length - 1)
    if (TRAILING_PUNCTUATION.has(last)) {
      s = s.slice(0, -1)
      continue
    }
    const opener = BRACKET_PAIRS[last]
    if (opener !== undefined && count(s, opener) < count(s, last)) {
      s = s.slice(0, -1)
      continue
    }
    return s
  }
  return s
}

const isEmail = (s: string): boolean => {
  const at = s.indexOf("@")
  if (at <= 0 || at !== s.lastIndexOf("@")) return false
  const domain = s.slice(at + 1)
  const dot = domain.indexOf(".")
  return dot > 0 && dot < domain.length - 1 && !/[\s@/\\]/.test(domain)
}

/**
 * The link a run of text without spaces would be read as: the opening bracket or quote in front left out, and the
 * punctuation and unmatched closing brackets at its end — `(https://x/y),` reads `https://x/y`. An address `known` to
 * be one — a link of the text a translation was made of — is read whole, though its end looks like punctuation: the
 * `?` closing `https://x/search?`. Whether it is a link at all is `hrefFor`'s to say.
 */
export const linkInToken = (token: string, known: readonly string[] = []): string => {
  let offset = 0
  while (offset < token.length && "([{\"'".includes(token.charAt(offset))) offset++
  const raw = token.slice(offset)
  const read = trimLinkEnd(raw)
  // The longest known address the trimming cut into; one shorter than what was read is another address.
  const whole = known
    .filter((address) => address.length > read.length && raw.startsWith(address))
    .reduce<
      string | null
    >((longest, address) => (longest === null || address.length > longest.length ? address : longest), null)
  return whole ?? read
}

/** The href for a recognised link, or null when the token is plain text. */
const hrefFor = (link: string): string | null => {
  const lower = link.toLowerCase()
  if (lower.startsWith("http://") || lower.startsWith("https://") || lower.startsWith("mailto:")) return link
  if (lower.startsWith("www.")) return `http://${link}`
  if (isEmail(link)) return `mailto:${link}`
  return null
}

/** A stretch of a text: words, or a link as it is written there with the href it opens. */
export interface TextPart {
  readonly text: string
  readonly href: string | null
}

/**
 * Splits `text` into words and the links among them, in order, each link as written with the href it opens. `known`
 * addresses are read whole (`linkInToken`).
 */
export function splitLinks(text: string, known: readonly string[] = []): TextPart[] {
  const parts: TextPart[] = []
  let last = 0
  for (const match of text.matchAll(TOKEN_PATTERN)) {
    // A token may carry an opening bracket or quote in front: `(https://x`, `[mailto:y`.
    const link = linkInToken(match[0], known)
    const href = link.length > 0 ? hrefFor(link) : null
    if (href === null) continue
    const start = match.index + match[0].indexOf(link)
    if (start > last) parts.push({ text: text.slice(last, start), href: null })
    parts.push({ text: link, href })
    last = start + link.length
  }
  if (last < text.length) parts.push({ text: text.slice(last), href: null })
  return parts
}

/**
 * Splits `text` into plain strings and rendered links, in order; a string without links comes back as `[text]`.
 * `known` addresses are read whole (`linkInToken`).
 */
export function linkifyText(text: string, renderLink: RenderLink, known: readonly string[] = []): ReactNode[] {
  let key = 0
  return splitLinks(text, known).map(({ text: words, href }) =>
    href === null ? words : renderLink(href, words, key++),
  )
}
